
<script setup lang="ts">
import QrScanner from 'qr-scanner';
import {ref} from "vue";
import {useContactsStore} from "~/stores/ContactsStore";
import {useRouter} from "#vue-router";
import WadSpinner from "~/components/common/wadSpinner.vue";
import type {EventAttendeeDirectBadgeScanRequest} from "~/types/Events";
import WadSwitch from "~/components/common/wadSwitch.vue";
import WadToast from "~/components/common/wadToast.vue";
import {useSettingsStore} from "~/stores/SettingsStore";

const settingsStore = useSettingsStore();
const offlineQueue = useOfflineLeadQueueStore();

const props = withDefaults(defineProps<{ leadScan?: boolean }>(), { leadScan: true });

const isOnline = useOnline();

const video = ref<HTMLVideoElement | null>(null);
const result = ref<string | null>(null);
const errorMessage = ref<string | null>(null);
const canFlip = ref(false);
const isLoadingQrCodeResponse = ref(false);
const openLead = computed({
  get: () => settingsStore.openLeadAfterScan,
  set: (value: boolean) => { settingsStore.openLeadAfterScan = value; },
});
const toast = ref<{type: 'success' | 'warning' | 'error', message: string} | null>(null);
const availableCameras = ref<{ id: string; label: string }[]>([]);
const hasFlash = ref(false);
const isFlashOn = ref(false);
let scanner: QrScanner | null = null;
let lastScannedCode: string | null = null;
let lastScanError: {type: 'error', message: string} | null = null;
let lastScannedAt = 0;
// While the same badge stays in frame it keeps decoding; ignore those re-reads for this
// window (sliding) instead of nagging with a "just scanned" warning on every frame.
const SAME_CODE_COOLDOWN_MS = 3000;

const activeCameraLabel = computed(() => {
  const cam = availableCameras.value.find(c => c.id === settingsStore.selectedCameraId);
  return cam?.label || null;
})

const isCalibrating = ref(false);

const wadError = useWadError();

// Multilingual label scoring
const BACK_CAMERA_PATTERNS = [
  /back|rear|environment/i,
  /rück|hinter/i, // DE
  /trasera|posterior/i, // ES
  /arrière/i, // FR
  /posteriore/i, // IT
  /traseira/i, // PT
  /后置/, // ZH
  /背面|アウト/, // JA
  /후면/, // KO
];

const WIDE_PENALIZE_PATTERNS = [
  /ultra.?wide|wide.?angle/i,
  /ultraweit|weitwinkel/i, // DE
  /gran.?angular|ultra.?gran/i, // ES
  /ultra.?grand.?angle/i, // FR
  /超広角/, // JA
  /超广角/, // ZH
];

const FRONT_PENALIZE_PATTERNS = [
  /front|user|selfie|facetime/i,
  /vorder|frontal/i, // DE
  /frontal|delantera/i, // ES
  /前置/, // ZH
  /前面|イン/, // JA
  /전면/, // KO
];

const TELEPHOTO_PENALIZE_PATTERNS = [
  /telephoto|tele/i,
  /macro/i,
  /triple|dual/i,
];

const scoreCameraLabel = (label: string): number => {
  let score = 0;
  if(BACK_CAMERA_PATTERNS.some(p => p.test(label))) {
    score += 5;
  }
  if(WIDE_PENALIZE_PATTERNS.some(p => p.test(label))) {
    score -= 8;
  }
  if(FRONT_PENALIZE_PATTERNS.some(p => p.test(label))) {
    score -= 20;
  }
  if(TELEPHOTO_PENALIZE_PATTERNS.some(p => p.test(label))) {
    score -= 5;
  }
  return score;
}

// Capabilities probing (expensive, used as fallback)
const probeCameraCapabilities = async (deviceId: string): Promise<number> => {
  let score = 0;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: deviceId } } });
    const track = stream.getVideoTracks()[0];
    const settings = track.getSettings?.() ?? {};
    const caps = (track as any).getCapabilities?.() ?? {};
    stream.getTracks().forEach(t => t.stop());

    // facingMode from hardware — most reliable signal
    if(settings.facingMode === 'environment') {
      score += 20;
    }else if(settings.facingMode === 'user') {
      score -= 20;
    }

    // Resolution as a proxy: depth/IR sensors have very low resolution
    const maxRes = (caps.width?.max ?? 0) * (caps.height?.max ?? 0);
    if(maxRes >= 1920 * 1080) {
      score += 5;
    }
    if(maxRes < 640 * 480) {
      score -= 15;
    }

    // Zoom: ultra-wide often has no zoom range, telephoto starts > 1
    const zoomMin = caps.zoom?.min ?? 1;
    const zoomMax = caps.zoom?.max ?? 1;
    if(zoomMin > 1.5) {
      score -= 3; // telephoto
    }
    if(zoomMax <= 1.5 && !caps.zoom) {
      score += 3; // standard lens
    }
  } catch {
    // Camera probe failed — no penalty, just skip
  }
  return score;
}

/**
 * Pick the best camera using a layered approach:
 * 1. facingMode from the active stream (free)
 * 2. Multilingual label scoring
 * 3. If inconclusive: probe capabilities (expensive, shows calibrating state)
 */
const pickBestCamera = async (cameras: { id: string; label: string }[], useActiveStreamHint = false): Promise<string | null> => {
  if(!cameras.length) {
    return null;
  }
  if(cameras.length === 1) {
    return cameras[0].id;
  }

  // Layer 3: Score by multilingual labels
  const scored = cameras.map(cam => ({
    id: cam.id,
    label: cam.label,
    score: scoreCameraLabel(cam.label),
  }));

  // Layer 1: Boost the active camera if it's rear-facing (only on initial mount)
  if(useActiveStreamHint && video.value?.srcObject instanceof MediaStream) {
    const activeTrack = video.value.srcObject.getVideoTracks()[0];
    const activeSettings = activeTrack?.getSettings?.();
    if(activeSettings?.facingMode === 'environment' && activeSettings?.deviceId) {
      const activeCam = scored.find(c => c.id === activeSettings.deviceId);
      if(activeCam) {
        activeCam.score += 20;
      }
    }
  }

  // Sort by score descending
  scored.sort((a, b) => b.score - a.score);

  const best = scored[0];
  const second = scored[1];

  // If clear winner (score gap ≥ 5), we're confident
  if(best.score - second.score >= 5) {
    return best.id;
  }

  // Inconclusive — fall back to probe each camera's capabilities
  isCalibrating.value = true;

  const probed = await Promise.all(scored.map(async (cam) => {
    const capScore = await probeCameraCapabilities(cam.id);
    return { id: cam.id, score: cam.score + capScore };
  }));

  isCalibrating.value = false;

  probed.sort((a, b) => b.score - a.score);
  return probed[0].id;
}

const switchToCamera = async (cameraId: string): Promise<void> => {
  if(!scanner) {
    return;
  }
  settingsStore.selectedCameraId = cameraId;
  await scanner.setCamera(cameraId);
  hasFlash.value = await scanner.hasFlash();
  if(!hasFlash.value) {
    isFlashOn.value = false;
  }
}

const cleanup = (): void => {
  if(scanner) {
    scanner.destroy();
    scanner = null;
  }
}

const flipCamera = async (): Promise<void> => {
  if(!canFlip.value || !scanner) {
    return;
  }

  const currentId = settingsStore.selectedCameraId;
  const currentIndex = availableCameras.value.findIndex(c => c.id === currentId);
  const nextIndex = (currentIndex + 1) % availableCameras.value.length;
  await switchToCamera(availableCameras.value[nextIndex].id);
}

const resetCamera = async (): Promise<void> => {
  if(!scanner) {
    return;
  }
  const bestId = await pickBestCamera(availableCameras.value);
  if(bestId) {
    await switchToCamera(bestId);
  }
}

const toggleFlash = async (): Promise<void> => {
  if(!scanner) {
    return;
  }
  try {
    await scanner.toggleFlash();
    isFlashOn.value = scanner.isFlashOn();
  } catch (e) {
    console.error('Flash toggle failed', e);
  }
}

onMounted(async () => {
  try {
    if(!video.value) {
      throw new Error('Video element not found');
    }

    const preferredCamera = settingsStore.selectedCameraId || 'environment';

    scanner = new QrScanner(
      video.value,
      (scanResult) => {
        if(!isLoadingQrCodeResponse.value) {
          result.value = scanResult.data;
        }
      },
      {
        preferredCamera,
        returnDetailedScanResult: true,
        highlightScanRegion: true,
        highlightCodeOutline: true,
        maxScansPerSecond: 5,
        onDecodeError: () => {},
        // Bias the scan region slightly below center (design 14511:49421) so it clears the overlay controls
        calculateScanRegion: (videoEl: HTMLVideoElement) => {
          const smallestDimension = Math.min(videoEl.videoWidth, videoEl.videoHeight);
          const scanRegionSize = Math.round(2 / 3 * smallestDimension);
          const centeredY = Math.round((videoEl.videoHeight - scanRegionSize) / 2);
          const offsetY = props.leadScan ? Math.round(0.06 * videoEl.videoHeight) : 0;
          return {
            x: Math.round((videoEl.videoWidth - scanRegionSize) / 2),
            y: Math.min(centeredY + offsetY, videoEl.videoHeight - scanRegionSize),
            width: scanRegionSize,
            height: scanRegionSize,
            downScaledWidth: 400,
            downScaledHeight: 400,
          };
        },
      }
    );

    await scanner.start();

    // Enumerate cameras after start() to leverage already-granted permission
    const cameras = await QrScanner.listCameras(true);
    availableCameras.value = cameras;
    canFlip.value = cameras.length > 1;

    // Smart-select best camera (skips if user already has a persisted choice)
    if(!settingsStore.selectedCameraId) {
      const bestId = await pickBestCamera(cameras, true);
      if(bestId) {
        await switchToCamera(bestId);
      }
    }

    if(scanner) {
      hasFlash.value = await scanner.hasFlash();
    }
  } catch (e: any) {
    errorMessage.value = e.message || 'Camera error';
    console.error(e);
  }
})

onBeforeUnmount(cleanup);

const router = useRouter();
const route = useRoute();
const contactsStore = useContactsStore();
const eventId = Number(route.params.eventId);

const {$api, $bugsnag} = useNuxtApp();

// Safety net for partner-ticket holders whose manager has not added them as a related
// person yet: this scanner only saves attendees as contacts, so warn them that it is
// not the lead scanner before they spend the event scanning "leads" that go nowhere.
const {needsLeadScanningWarning} = useWadAuth();
const showLeadScanWarning = ref(!props.leadScan && needsLeadScanningWarning(eventId));

const acknowledgeLeadScanWarning = async () => {
  showLeadScanWarning.value = false;
  try {
    // The backend records only the first confirmation, so this is safe to send every time.
    await $api.v2.acknowledgeLeadScanningNotice(eventId);
  } catch (e) {
    $bugsnag?.notify(e as Error);
  }
};

watch(result, async () => {
  if(!result.value || !scanner) {
    return;
  }

  const code = result.value;

  // Same ticket scanned consecutively.
  if(code === lastScannedCode) {
    result.value = null;
    // If it failed last time, keep surfacing that error so the failure isn't missed.
    if(lastScanError) {
      toast.value = lastScanError;
      return;
    }
    // Otherwise it's the same badge still in frame — stay silent during the cooldown
    // (sliding) rather than warning on every re-read.
    if(Date.now() - lastScannedAt < SAME_CODE_COOLDOWN_MS) {
      lastScannedAt = Date.now();
      return;
    }
  }

  isLoadingQrCodeResponse.value = true;

  try {
    if(!code.startsWith('ti_')) {
      toast.value = {type: 'error', message: 'Ticket not found!'};
      return;
    }

    lastScannedCode = code;
    lastScannedAt = Date.now();
    lastScanError = null;
    toast.value = null;

    if(props.leadScan) {
      // Offline path: save locally and skip API call
      if(!isOnline.value) {
        const pending = offlineQueue.enqueue(eventId, code);
        if(pending) {
          toast.value = {type: 'warning', message: "Lead saved on device – you're offline"};
        } else {
          toast.value = {type: 'warning', message: 'QR code has already been scanned.'};
        }
        return;
      }

      // Online path
      const lead = await contactsStore.scanContact(eventId, code);
      if(lead && openLead.value) {
        return router.push({ name: 'events-eventId-leads-id', params: { eventId: eventId, id: lead.id }});
      }
      if(lead.new_scan) {
        toast.value = {type: 'success', message: 'Lead successfully saved'};
      } else {
        toast.value = {type: 'warning', message: 'This lead is already in your list.'};
      }
    } else {
      const payload: EventAttendeeDirectBadgeScanRequest = {
        ticket_identification: code,
      };
      const resp = await $api.v2.attendeeDirectBadgeScan(eventId, payload);
      if(resp) {
        toast.value = {type: 'success', message: 'Contact added to your list'};
        try {
          await router.push({ name: 'events-eventId-attendees-userId-contact-details', params: { eventId: eventId, userId: resp.user_id }});
        } catch(navError: any) {
          // Contact is already saved above — only navigation failed, so keep the success
          // toast rather than letting this fall through to the generic error toast below.
          $bugsnag?.notify(navError as Error);
        }
      }
    }
  } catch(e: any) {
    // Fallback: connectivity dropped mid-request. Checks the failure itself as well as the
    // connectivity state, because the state is only corrected by a probe a moment later - the
    // scan that exposed the drop must still be queued rather than reported as an error.
    if((!isOnline.value || isConnectivityError(e)) && props.leadScan && code.startsWith('ti_')) {
      offlineQueue.enqueue(eventId, code);
      toast.value = {type: 'warning', message: "Lead saved on device – you're offline"};
    } else {
      // Passes the rejection itself. The parser unwraps the body now, and pre-unwrapping here
      // would hide the status it needs.
      const parsed = wadError.parseErrorFromException(e);
      lastScanError = {type: 'error', message: parsed?.message || 'Something went wrong. Please try again.'};
      toast.value = lastScanError;
    }
  } finally {
    result.value = null;
    // Re-enable scanning quickly so user can scan next ticket
    setTimeout(() => { isLoadingQrCodeResponse.value = false; }, 300);
  }
});
</script>

<template>
  <div class="wad4-qr-scanner">
    <div class="wad4-qr-scanner__camera" :class="{'wad4-qr-scanner__camera--full-bleed': leadScan}">
      <video ref="video" class="wad4-qr-scanner__camera-feed" :class="{'wad4-qr-scanner__camera-feed--hidden': isCalibrating}" autoplay playsinline muted></video>
      <div class="wad4-qr-scanner__camera-overlay">
        <div class="wad4-qr-scanner__camera-controls">
          <button v-if="canFlip" type="button" class="wad4-button-secondary-grey wad4-qr-scanner__camera-control" @click="flipCamera">
            <svg width="24" height="24" viewBox="-1.5 -3 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill="currentColor" d="M19.5 6.57421C19.5 5.85608 18.9978 5.25757 18.3252 5.1455C17.9549 5.08381 17.5836 5.02548 17.2119 4.97265C16.77 4.91214 16.3468 4.75596 15.9717 4.51464C15.5965 4.2733 15.2783 3.95327 15.04 3.57617L15.0381 3.57226L14.2158 2.25683L14.2129 2.25195C14.0915 2.05488 13.9242 1.88938 13.7256 1.7705C13.5269 1.65169 13.3024 1.58313 13.0713 1.56933C11.358 1.47747 9.641 1.47646 7.92773 1.56836C7.69697 1.58229 7.47283 1.65184 7.27441 1.7705C7.07579 1.88938 6.90853 2.05488 6.78711 2.25195L6.78418 2.25586L5.96289 3.57226L5.96094 3.57617C5.72253 3.95341 5.40466 4.27427 5.0293 4.51562C4.65377 4.75704 4.22945 4.91336 3.78711 4.97363L3.78613 4.97265C3.41367 5.02562 3.04459 5.0829 2.67578 5.14453L2.6748 5.1455C2.0015 5.25751 1.5 5.85572 1.5 6.57421V15C1.5 15.3978 1.65821 15.7792 1.93945 16.0605C2.22076 16.3418 2.60218 16.5 3 16.5H18C18.3978 16.5 18.7792 16.3418 19.0605 16.0605C19.3418 15.7792 19.5 15.3978 19.5 15V6.57421ZM21 15C21 15.7956 20.6836 16.5585 20.1211 17.1211C19.5585 17.6837 18.7957 18 18 18H3C2.20435 18 1.44151 17.6837 0.878906 17.1211C0.316361 16.5585 0 15.7956 0 15V6.57421C0 5.15887 0.996271 3.90323 2.42871 3.66503L3.58008 3.4873H3.58496C3.81035 3.45656 4.02642 3.3769 4.21777 3.2539C4.40796 3.13163 4.56913 2.96908 4.69043 2.77832L5.51172 1.46191C5.75927 1.06123 6.09975 0.726287 6.50391 0.484371C6.90937 0.241702 7.36718 0.0995174 7.83887 0.0712848H7.84375C9.61329 -0.0237616 11.3867 -0.0237616 13.1562 0.0712848H13.1611C13.6328 0.0995174 14.0906 0.241702 14.4961 0.484371C14.9001 0.7262 15.2398 1.06143 15.4873 1.46191L16.3105 2.77832C16.4318 2.96903 16.5931 3.13162 16.7832 3.2539C16.9744 3.37685 17.1908 3.4565 17.416 3.4873H17.4199C17.8044 3.54193 18.1882 3.60121 18.5713 3.66503C20.0024 3.90318 21 5.15852 21 6.57421V15Z"/>
              <path fill="currentColor" d="M14.25 9.7502C14.25 8.75564 13.8546 7.8021 13.1514 7.09883C12.4481 6.39557 11.4946 6.0002 10.5 6.0002C9.50544 6.0002 8.55189 6.39557 7.84863 7.09883C7.14537 7.8021 6.75 8.75564 6.75 9.7502C6.75 10.7448 7.14537 11.6983 7.84863 12.4016C8.55189 13.1048 9.50544 13.5002 10.5 13.5002C11.4946 13.5002 12.4481 13.1048 13.1514 12.4016C13.8546 11.6983 14.25 10.7448 14.25 9.7502ZM17.2578 6.7502C17.672 6.7502 18.0078 7.08599 18.0078 7.5002V7.50801C18.0078 7.92223 17.672 8.25801 17.2578 8.25801H17.25C16.8358 8.25801 16.5 7.92223 16.5 7.50801V7.5002C16.5 7.08599 16.8358 6.7502 17.25 6.7502H17.2578ZM15.75 9.7502C15.75 11.1426 15.1965 12.4776 14.2119 13.4621C13.2273 14.4467 11.8924 15.0002 10.5 15.0002C9.10761 15.0002 7.77265 14.4467 6.78809 13.4621C5.80352 12.4776 5.25 11.1426 5.25 9.7502C5.25 8.35781 5.80352 7.02285 6.78809 6.03829C7.77265 5.05372 9.10761 4.5002 10.5 4.5002C11.8924 4.5002 13.2273 5.05372 14.2119 6.03829C15.1965 7.02285 15.75 8.35781 15.75 9.7502Z"/>
            </svg>
            <span class="wad4-button__text">Camera</span>
          </button>
          <button v-if="hasFlash" type="button" class="wad4-button-secondary-grey wad4-qr-scanner__camera-control" @click="toggleFlash">
            <svg v-if="isFlashOn" width="24" height="24" viewBox="-2.25 -1.5 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.43842 13.9581C8.54739 13.5584 8.9597 13.3227 9.35932 13.4317C9.75887 13.5407 9.99464 13.953 9.88569 14.3526L9.02143 17.5206L10.6962 15.7266C10.9788 15.4239 11.454 15.407 11.7568 15.6895C12.0596 15.9722 12.0756 16.4473 11.7929 16.7501L8.0478 20.7618C7.81247 21.0137 7.43585 21.0732 7.13471 20.9053C6.8335 20.7374 6.68561 20.3855 6.77631 20.0528L8.43842 13.9581ZM11.4521 0.238342C11.6874 -0.0135954 12.064 -0.0730629 12.3652 0.0947878C12.6664 0.262715 12.8143 0.614612 12.7236 0.947327L10.7324 8.25006H17.9999C18.2986 8.25006 18.5684 8.42736 18.6874 8.70123C18.8065 8.97514 18.7516 9.29345 18.5478 9.51178L14.3349 14.0245L19.2802 18.9698C19.5731 19.2627 19.5731 19.7374 19.2802 20.0303C18.9873 20.3232 18.5126 20.3232 18.2197 20.0303L0.21967 2.03033C-0.0732233 1.73744 -0.0732233 1.26268 0.21967 0.969788C0.512563 0.676895 0.987324 0.676895 1.28022 0.969788L6.18842 5.87799L11.4521 0.238342ZM13.2744 12.9639L16.2744 9.75006H10.0605L13.2744 12.9639ZM3.61029 8.63971C3.89292 8.33689 4.36803 8.31998 4.67084 8.6026C4.97365 8.88522 4.98959 9.36033 4.70697 9.66315L3.22553 11.2501H7.00678C7.42099 11.2501 7.75678 11.5858 7.75678 12.0001C7.75678 12.4143 7.42099 12.7501 7.00678 12.7501H1.49994C1.20132 12.7501 0.931483 12.5728 0.812443 12.2989C0.693417 12.025 0.748323 11.7067 0.952092 11.4883L3.61029 8.63971ZM7.24897 6.93854L9.04487 8.73444L10.4775 3.47858L7.24897 6.93854Z" fill="currentColor"/>
            </svg>
            <svg v-else width="24" height="24" viewBox="-3 -1.5 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.7018 0.238343C10.9371 -0.0135958 11.3137 -0.0730629 11.6149 0.094788C11.9161 0.262717 12.064 0.614615 11.9733 0.947327L9.98206 8.25006H17.2496C17.5483 8.25006 17.8181 8.42736 17.9371 8.70123C18.0562 8.97514 18.0013 9.29345 17.7975 9.51178L7.29748 20.7618C7.06216 21.0137 6.68554 21.0732 6.3844 20.9053C6.08319 20.7374 5.9353 20.3855 6.026 20.0528L8.01721 12.7501H0.749634C0.451008 12.7501 0.181173 12.5728 0.0621335 12.2989C-0.056892 12.025 -0.0019908 11.7067 0.201782 11.4883L10.7018 0.238343ZM2.47522 11.2501H8.99963C9.23337 11.2501 9.45345 11.3592 9.59534 11.545C9.73721 11.7307 9.78474 11.9718 9.72327 12.1973L8.27112 17.5206L15.524 9.75006H8.99963C8.7659 9.75006 8.54582 9.64087 8.40393 9.45514C8.26206 9.2694 8.21453 9.02829 8.276 8.8028L9.72717 3.47858L2.47522 11.2501Z" fill="currentColor"/>
            </svg>
            <span class="wad4-button__text">Flash {{ isFlashOn ? 'off' : 'on' }}</span>
          </button>
        </div>
        <div v-if="activeCameraLabel" class="wad4-qr-scanner__hints-hint">
          Using: {{ activeCameraLabel }}
          <template v-if="canFlip">
            · <span @click="resetCamera">Reset to default</span>
          </template>
        </div>
        <div v-if="!isOnline && leadScan" class="wad4-qr-scanner__camera-offline">
          <strong>Offline mode –&nbsp;</strong>Scans are saved to your device
        </div>
      </div>
      <div v-if="isCalibrating" class="wad4-qr-scanner__camera-calibrating">
        <wad-spinner />
        <span>Calibrating camera...</span>
      </div>
      <div v-if="isLoadingQrCodeResponse" class="wad4-qr-scanner__camera-loading">
        <wad-spinner />
      </div>
      <div class="wad4-qr-scanner__camera-error">{{ errorMessage }}</div>
      <div v-if="!leadScan" class="wad4-qr-scanner__camera-prompt">Scan code to save a contact</div>
    </div>
    <client-only>
      <!-- Teleported to <body> so the fixed toast escapes this page's overflow/stacking subtree.
           Inside it, iOS standalone WebKit mis-stacks the fixed toast under the opaque action bar
           (works in-browser, fails in the installed PWA); at the body root it paints reliably. -->
      <Teleport to="body">
        <div v-if="toast" class="wad4-qr-scanner__toast">
          <wad-toast
            :type="toast.type"
            :message="toast.message"
            @close="toast = null"
          />
        </div>
      </Teleport>
    </client-only>
    <client-only>
      <common-wad-modal v-model="showLeadScanWarning" :show-close-icon="false" :click-to-close="false" title="Lead scanning not enabled!">
        <template #content>
          <p class="wad4-qr-scanner__lead-warning-text">This page is not intended for lead scanning — badges scanned here are only saved as contacts.</p>
          <p class="wad4-qr-scanner__lead-warning-text">Please contact your manager to add you as a related person in the WAD portal, in order to be able to scan leads at the event, using this app.</p>
        </template>
        <template #controls>
          <button type="button" class="wad4-button-primary wad4-button--width-full" @click="acknowledgeLeadScanWarning">
            <span class="wad4-button__text">I understand</span>
          </button>
        </template>
      </common-wad-modal>
    </client-only>
    <div v-if="leadScan" class="wad4-qr-scanner__switch">
      <div class="wad4-qr-scanner__switch-row">
        <span class="wad4-qr-scanner__switch-label">Open lead after scan</span>
        <wad-switch v-model="openLead"></wad-switch>
      </div>
      <div class="wad4-qr-scanner__switch-description">
        Navigate to contact for editing.<br>Turn off for bulk scanning leads.
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.wad4-qr-scanner {
  display: flex;
  width: 100%;
  max-width: 500px;
  flex-direction: column;
  align-items: center;
  gap: $wad4-spacing-lg;

  // Pinned to the bottom so the scan feedback stays visible instead of getting cut off below the
  // camera area. env() keeps it clear of the iOS home indicator.
  &__toast {
    position: fixed;
    left: 0;
    right: 0;
    bottom: calc(env(safe-area-inset-bottom, 0px) + #{$wad4-spacing-lg});
    // Teleported to <body>; sit above the page action bar.
    z-index: 1000;
    max-width: $wad4-breakpoint-desktop-min;
    margin: 0 auto;
    padding: 0 $wad4-container-padding;
    pointer-events: none;

    > * {
      pointer-events: auto;
    }
  }

  &__lead-warning-text {
    margin: unset;
    color: $wad4-c-text-text-primary;
  }

  &__switch {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: $wad4-spacing-xs;
    padding: $wad4-spacing-lg;
    border-radius: $wad4-border-radius-md;
    border: 1px solid $wad4-c-border-border-subtle;
    background: $wad4-c-background-bg-primary;

    &-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: $wad4-spacing-xs;
    }

    &-label {
      overflow: hidden;
      color: $wad4-c-text-text-primary;
      text-overflow: ellipsis;
      white-space: nowrap;
      @include text-size-mixin('md');
      font-weight: $wad4-font-weight-semi-bold;
    }

    &-description {
      color: $wad4-c-text-text-primary;
      @include text-size-mixin('xs');
    }
  }

  &__camera {
    width: 100%;
    position: relative;
    overflow: hidden;

    &--full-bleed {
      margin: 0 (-$wad4-spacing-lg);
      width: calc(100% + #{2 * $wad4-spacing-lg});

      .wad4-qr-scanner__camera-feed {
        border: none;
        border-radius: 0;
        height: 377px;
        object-fit: cover;

        // Shrink the camera on short screens so the toggle/info and action bar below it
        // stay visible once the scan page flows into a document scroll (see scan.vue).
        @media screen and (max-height: $wad4-breakpoint-short-screen-max) {
          height: 300px;
        }
      }

      @include mqBreakPoint($wad4-breakpoint-tablet-min) {
        margin: 0;
        width: 100%;

        .wad4-qr-scanner__camera-feed {
          border-radius: $wad4-border-radius-md;
        }
      }
    }

    &-feed {
      border-radius: $wad4-border-radius-md;
      border: 2px solid $wad4-c-border-border-brand-solid;
      width: 100%;
      height: auto;
      display: block;

      &--hidden {
        opacity: 0;
      }
    }

    &-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      display: flex;
      flex-direction: column;
      gap: $wad4-spacing-md;
      padding: $wad4-spacing-lg;
    }

    &-controls {
      display: flex;
      gap: $wad4-spacing-lg;
    }

    &-control {
      flex: 1 0 0;
      max-width: calc(50% - #{$wad4-spacing-sm});
      justify-content: center;
      white-space: nowrap;

      // With both buttons present each is capped at ~50% width, so the inner flex
      // (icon + nowrap label) has to give somewhere. Pin the icon at full size and
      // never let it shrink — it's the only shrinkable item, so it was the one that
      // collapsed (most visibly on iOS Safari, which also ignores the SVG's
      // width/height attributes inside a flex container).
      svg {
        flex-shrink: 0;
        width: 24px;
        height: 24px;
      }

      // Let the label absorb the squeeze instead of the icon: truncate rather than
      // overflow the button when space is tight.
      .wad4-button__text {
        overflow: hidden;
        text-overflow: ellipsis;
        min-width: 0;
      }
    }

    &-offline {
      display: flex;
      align-items: center;
      align-self: center;
      max-width: 100%;
      overflow: hidden;
      white-space: nowrap;
      padding: $wad4-spacing-xxs $wad4-spacing-sm;
      border-radius: $wad4-border-radius-xs;
      background: $wad4-c-background-bg-warning-primary;
      color: $wad4-c-text-text-warning;
      @include text-size-mixin('sm');

      strong {
        font-weight: $wad4-font-weight-semi-bold;
      }
    }

    &-prompt {
      position: absolute;
      bottom: $wad4-spacing-lg;
      left: 0;
      width: 100%;
      text-align: center;
      color: $wad4-c-text-text-white;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
      @include text-size-mixin('xs');
    }

    &-loading {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &-calibrating {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: $wad4-spacing-xs;
      color: $wad4-c-text-text-primary;
      @include text-size-mixin('xs');
    }

    &-error {
      position: absolute;
      width: 100%;
      top: calc(50% - 10px);
      left: 0;
      text-align: center;
      color: $wad4-c-text-text-error;
      font-weight: $wad4-font-weight-semi-bold;
    }
  }

  // Camera info sits inside the camera overlay (under the controls), so it needs the
  // same white + shadow treatment as the on-camera prompt to stay readable over the feed.
  &__hints-hint {
    width: 100%;
    overflow: hidden;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: $wad4-c-text-text-white;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
    @include text-size-mixin('xs');

    span {
      cursor: pointer;
      text-decoration: underline;
    }
  }
}
</style>
